import React, { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../redux/hooks";
import { logoutSuccess, selectUser } from "../../redux/modules/userSlice";
import { performLogout } from "../api";
import Uploadphotoicon from "../../Assests/Images/uploadphotoicon.png";
import spanideaLogo from "../../Assests/Images/auth/spanidea-logo.svg";
import AdminIcon from "./adminIcons";
import { ADMIN_COLORS, ADMIN_FONT, ADMIN_HEADER_HEIGHT, ADMIN_TYPE } from "./adminUi";
import { adminHomePath } from "./adminRoutes";

/** Logo artwork is 159.444 × 20; keep that ratio when resizing. */
var LOGO_WIDTH = 144;
var LOGO_HEIGHT = 18;
var AVATAR_SIZE = 36;

/** Admin app bar for HR and Super Admin (no sidebar, no notifications). */
function AdminHeader() {
  var navigate = useNavigate();
  var dispatch = useAppDispatch();
  var { user } = useSelector(selectUser);
  var [anchorEl, setAnchorEl] = useState(null);

  if (!user || !user._id || !(user.isAdmin || user.isSuperAdmin)) {
    return null;
  }

  var hasBothRoles = Boolean(user.isAdmin && user.isSuperAdmin);
  var photo = user.profilePhoto || Uploadphotoicon;
  var displayName = user.name || (user.isSuperAdmin ? "Super Admin" : "HR");

  function closeMenu() {
    setAnchorEl(null);
  }

  function go(path) {
    closeMenu();
    navigate(path);
  }

  function handleLogout() {
    closeMenu();
    performLogout().then(
      function () {
        dispatch(logoutSuccess());
        navigate("/login");
      },
      function () {
        dispatch(logoutSuccess());
        navigate("/login");
      }
    );
  }

  return (
    <Box
      component="header"
      sx={{
        height: ADMIN_HEADER_HEIGHT,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: "12px",
        px: "20px",
        bgcolor: ADMIN_COLORS.card,
        borderBottom: "1px solid " + ADMIN_COLORS.line,
        boxSizing: "border-box",
        position: "relative",
        zIndex: 2,
      }}
    >
      <ButtonBase
        onClick={function () {
          navigate(adminHomePath(user));
        }}
        aria-label="Go to home"
        sx={{ display: "flex", alignItems: "center", gap: "12px", borderRadius: "4px" }}
      >
        <Box
          component="img"
          src={spanideaLogo}
          alt="Spanidea"
          sx={{ width: LOGO_WIDTH, height: LOGO_HEIGHT, display: "block" }}
        />
        <Box sx={{ width: "1px", height: 28, bgcolor: ADMIN_COLORS.line }} />
        <Typography
          sx={{
            fontFamily: ADMIN_FONT,
            fontSize: ADMIN_TYPE.appName.fontSize,
            lineHeight: ADMIN_TYPE.appName.lineHeight,
            fontWeight: ADMIN_TYPE.appName.fontWeight,
            color: ADMIN_COLORS.label,
            whiteSpace: "nowrap",
            display: { xs: "none", sm: "block" },
          }}
        >
          HR Onboarding Admin
        </Typography>
      </ButtonBase>

      <Box sx={{ flex: 1 }} />

      <ButtonBase
        onClick={function (e) {
          setAnchorEl(e.currentTarget);
        }}
        aria-label="Open profile menu"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "6px",
          py: "4px",
          borderRadius: "4px",
          "&:hover": { bgcolor: ADMIN_COLORS.page },
        }}
      >
        <Avatar src={photo} alt="" sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }} />
        <Typography
          sx={{
            fontFamily: ADMIN_FONT,
            fontSize: ADMIN_TYPE.userName.fontSize,
            lineHeight: ADMIN_TYPE.userName.lineHeight,
            fontWeight: ADMIN_TYPE.userName.fontWeight,
            color: ADMIN_COLORS.label,
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayName}
        </Typography>
        <AdminIcon
          name="CaretRight"
          size={18}
          sx={{
            color: ADMIN_COLORS.label,
            transform: anchorEl ? "rotate(-90deg)" : "rotate(90deg)",
            transition: "transform 150ms",
          }}
        />
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: "6px",
            minWidth: 220,
            borderRadius: "8px",
            border: "1px solid " + ADMIN_COLORS.lineSoft,
            boxShadow: "0 8px 24px rgba(23, 26, 28, 0.12)",
            "& .MuiMenuItem-root": {
              gap: "10px",
              px: "14px",
              py: "8px",
              fontFamily: ADMIN_FONT,
              fontSize: 14,
              color: ADMIN_COLORS.label,
              "&:hover": { bgcolor: ADMIN_COLORS.brandSoft },
            },
          },
        }}
      >
        <Box sx={{ px: "14px", py: "8px", minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: 500, color: ADMIN_COLORS.label }}>
            {displayName}
          </Typography>
          <Typography noWrap sx={{ fontFamily: ADMIN_FONT, fontSize: 12, color: ADMIN_COLORS.labelMuted }}>
            {user.email || ""}
          </Typography>
        </Box>
        <Divider sx={{ my: "4px", borderColor: ADMIN_COLORS.lineSoft }} />
        {hasBothRoles ? (
          <MenuItem
            onClick={function () {
              go("/AdminEmployees");
            }}
          >
            <PeopleOutlineIcon sx={{ fontSize: 18, color: ADMIN_COLORS.labelMuted }} />
            Manage New Joinee
          </MenuItem>
        ) : null}
        {hasBothRoles ? (
          <MenuItem
            onClick={function () {
              go("/AdminUsers");
            }}
          >
            <PeopleOutlineIcon sx={{ fontSize: 18, color: ADMIN_COLORS.labelMuted }} />
            Manage HR
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={function () {
            go("/AdminProfile");
          }}
        >
          <PersonOutlineOutlinedIcon sx={{ fontSize: 18, color: ADMIN_COLORS.labelMuted }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ "&&": { color: ADMIN_COLORS.fieldError } }}>
          <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default AdminHeader;
